import axios from 'axios';
import { AccountInfo, AuthenticationResult } from '@azure/msal-browser';
import { msalInstance } from './msalInstance';
import { loginRequest, updateAuthority } from 'utils/constants/authConfig';
import { UserClaims } from 'interfaces/auth';

let initialized = false;
let initializing = false;

const ensureInitialized = async (): Promise<void> => {
  if (initialized) {
    return;
  }

  if (initializing) {
    // Wait for the current initialization to complete
    while (initializing) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    return;
  }

  try {
    initializing = true;
    await msalInstance.initialize();
    initialized = true;
  } finally {
    initializing = false;
  }
};

export const signIn = async (): Promise<void> => {
  try {
    await ensureInitialized();
    updateAuthority('signUpSignIn');
    await msalInstance.loginRedirect(loginRequest);
  } catch (error) {
    console.error('Sign in error:', error);
    throw new Error('Failed to sign in');
  }
};

export const signOut = async (): Promise<void> => {
  try {
    await ensureInitialized();
    const account = msalInstance.getActiveAccount();
    if (account) {
      await msalInstance.logoutRedirect({ account });
    }
  } catch (error) {
    console.error('Sign out error:', error);
    throw new Error('Failed to sign out');
  }
};

export const handleRedirect = async (): Promise<AccountInfo | null> => {
  try {
    await ensureInitialized();

    const result: AuthenticationResult | null =
      await msalInstance.handleRedirectPromise();

    if (result) {
      msalInstance.setActiveAccount(result.account);
      return result.account;
    } else {
      const currentAccount = msalInstance.getAllAccounts()[0];
      if (currentAccount) {
        msalInstance.setActiveAccount(currentAccount);
        return currentAccount;
      }
    }

    return null;
  } catch (error: any) {
    // Handle specific Azure B2C error codes
    if (error.errorMessage?.includes('AADB2C90118')) {
      // Password reset flow
      updateAuthority('passwordReset');
      await msalInstance.loginRedirect(loginRequest);
      return null;
    }

    // Handle user cancellation and other non-critical errors
    if (
      error.errorCode === 'access_denied' ||
      error.errorMessage?.includes('AADB2C90091') ||
      error.errorMessage?.includes('user_cancelled') ||
      error.errorMessage?.includes('cancelled')
    )
      return null;

    if (
      error.errorCode === 'interaction_required' ||
      error.errorMessage?.includes('interaction_required')
    )
      return null;

    console.error('Redirect handling error:', error);
    throw new Error('Failed to handle redirect');
  }
};

export const getToken = async (): Promise<string> => {
  try {
    const account = msalInstance.getActiveAccount();
    if (!account) {
      throw new Error('No active account');
    }

    const tokenResponse = await msalInstance.acquireTokenSilent({
      scopes: loginRequest.scopes,
      account
    });

    return tokenResponse.accessToken;
  } catch (error) {
    try {
      const tokenResponse = await msalInstance.acquireTokenPopup(loginRequest);
      return tokenResponse.accessToken;
    } catch (popupError) {
      console.error('Token acquisition error:', popupError);
      throw new Error('Failed to acquire token');
    }
  }
};

export const setAuthCookie = async () => {
  const account = msalInstance.getActiveAccount();

  if (account) {
    try {
      const response = await msalInstance.acquireTokenSilent({
        account,
        scopes: loginRequest.scopes
      });

      await axios.post('/api/setAuthCookie', {
        accessToken: response.accessToken
      });
    } catch (e) {
      console.error('Silent set token failed', e);
    }
  }
};

export const getUserClaims = (): UserClaims | null => {
  const account = msalInstance.getActiveAccount();
  if (!account?.idTokenClaims) {
    return null;
  }

  return account.idTokenClaims;
};

export const isUserApproved = (): boolean => {
  const claims = getUserClaims();

  return claims?.extension_IsApproved === true;
};

export const isUserPortCo = (): boolean => {
  const claims = getUserClaims();

  return claims?.extension_isPcu === true;
};

export const getCompanyName = (): string => {
  const claims = getUserClaims();

  return claims?.extension_CompanyName as string;
};

export const getUsername = (): string => {
  const account = msalInstance.getActiveAccount();

  if (!account?.idTokenClaims) {
    return 'User';
  }

  const username =
    account?.name ||
    account?.username ||
    account?.idTokenClaims?.emails?.[0]?.split('@')?.[0] ||
    'user';

  return username.charAt(0).toUpperCase() + username.slice(1);
};
